import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { useFiscalYearStore, type FiscalYear } from '../../store/fiscalYearStore'
import { useAuthStore } from '../../store/authStore'
import { useLanguageStore } from '../../store/languageStore'
import { useTabStore } from '../../store/tabStore'
import { useNavigate } from 'react-router-dom'
import { FiCalendar, FiChevronDown, FiCheckCircle, FiRefreshCw } from 'react-icons/fi'
import { formatDateRange } from '../../utils/formatters'

const DROPDOWN_WIDTH = 320

export default function FiscalYearSelector() {
  const { language } = useLanguageStore()
  const { user } = useAuthStore()
  const { selectedFiscalYear, setSelectedFiscalYear } = useFiscalYearStore()
  const { addTab } = useTabStore()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({})
  const triggerRef = useRef<HTMLDivElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const isRTL = language === 'ar'

  const companyId = user?.companyId || user?.companyCode || ''

  const { data: fiscalYears = [], isLoading, refetch } = useQuery<FiscalYear[]>({
    queryKey: ['fiscalYears', companyId],
    queryFn: async () => {
      if (!companyId) return []

      try {
        const endpoint = API_ENDPOINTS.fiscalYears.list(companyId)
        const response = await apiClient.get(endpoint)
        const raw = response.data
        const data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.items)
              ? raw.items
              : []

        return data.map((fy: any) => ({
          id: fy.id || fy.ID,
          company_ID: fy.company_ID || fy.companyId,
          name: fy.name || fy.Name,
          startDate: fy.startDate || fy.StartDate,
          endDate: fy.endDate || fy.EndDate,
          isClosed: fy.isClosed || fy.IsClosed || false,
          closedDate: fy.closedDate || fy.ClosedDate,
          closedBy: fy.closedBy || fy.ClosedBy,
          notes: fy.notes || fy.Notes,
        }))
      } catch (error) {
        console.error('Error fetching fiscal years:', error)
        return []
      }
    },
    enabled: !!companyId,
  })

  useEffect(() => {
    if (!selectedFiscalYear && companyId && fiscalYears.length > 0) {
      const activeFiscalYear = fiscalYears.find(fy => !fy.isClosed)
      if (activeFiscalYear) {
        setSelectedFiscalYear(activeFiscalYear)
      } else if (fiscalYears.length > 0) {
        const sortedFiscalYears = [...fiscalYears].sort((a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
        )
        setSelectedFiscalYear(sortedFiscalYears[0])
      }
    }
  }, [selectedFiscalYear, companyId, fiscalYears, setSelectedFiscalYear])

  const updatePopupPosition = useCallback(() => {
    if (!triggerRef.current) return

    const rect = triggerRef.current.getBoundingClientRect()
    const margin = 8

    // بعيداً عن المنيو: في العربية المنيو يمين → القائمة تمتد لليسار
    // في الإنجليزية المنيو يسار → القائمة تمتد لليمين
    if (isRTL) {
      setPopupStyle({
        position: 'fixed',
        zIndex: 99999,
        width: DROPDOWN_WIDTH,
        top: rect.bottom + margin,
        right: window.innerWidth - rect.right,
        left: 'auto',
      })
    } else {
      setPopupStyle({
        position: 'fixed',
        zIndex: 99999,
        width: DROPDOWN_WIDTH,
        top: rect.bottom + margin,
        left: rect.left,
        right: 'auto',
      })
    }
  }, [isRTL])

  useEffect(() => {
    if (!isOpen) return

    updatePopupPosition()
    window.addEventListener('scroll', updatePopupPosition, true)
    window.addEventListener('resize', updatePopupPosition)

    return () => {
      window.removeEventListener('scroll', updatePopupPosition, true)
      window.removeEventListener('resize', updatePopupPosition)
    }
  }, [isOpen, updatePopupPosition])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current?.contains(target) ||
        portalRef.current?.contains(target)
      ) {
        return
      }
      setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear)
    setIsOpen(false)
  }

  const handleManageFiscalYears = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsOpen(false)

    const tabId = 'fiscal-years'
    const title = isRTL ? 'السنوات المالية' : 'Fiscal Years'
    const path = '/fiscal-years'

    const opened = addTab({ id: tabId, title, basePath: path, lastPath: path })
    if (opened) navigate(path)
  }

  if (!companyId) {
    return null
  }

  const dropdown = isOpen ? (
    <div
      ref={portalRef}
      className="bg-gray-800 rounded-lg shadow-2xl border border-gray-700 max-h-[400px] overflow-hidden flex flex-col"
      style={popupStyle}
    >
      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">
          {isRTL ? 'السنة المالية' : 'Fiscal Year'}
        </h3>
        <button
          onClick={(e) => {
            e.stopPropagation()
            refetch()
          }}
          className="p-1 hover:bg-gray-700 rounded transition-colors"
          title={isRTL ? 'تحديث' : 'Refresh'}
        >
          <FiRefreshCw className="text-gray-400 text-sm" />
        </button>
      </div>

      <div className="overflow-y-auto max-h-[300px]">
        {isLoading ? (
          <div className="px-4 py-8 text-center text-gray-400">
            {isRTL ? 'جاري التحميل...' : 'Loading...'}
          </div>
        ) : fiscalYears.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400">
            {isRTL ? 'لا توجد سنوات مالية' : 'No fiscal years'}
          </div>
        ) : (
          <div className="py-2">
            {fiscalYears
              .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
              .map((fiscalYear) => (
                <button
                  key={fiscalYear.id}
                  onClick={() => handleSelect(fiscalYear)}
                  className={`w-full px-4 py-3 hover:bg-gray-700 transition-colors ${
                    isRTL ? 'text-right' : 'text-left'
                  } ${selectedFiscalYear?.id === fiscalYear.id ? 'bg-purple-900/30' : ''}`}
                >
                  <div className={`flex items-start justify-between gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="flex-1 min-w-0">
                      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                        <span className={`text-sm font-medium ${
                          selectedFiscalYear?.id === fiscalYear.id ? 'text-purple-300' : 'text-white'
                        }`}>
                          {fiscalYear.name}
                        </span>
                        {fiscalYear.isClosed && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-900/30 text-red-300">
                            {isRTL ? 'مغلقة' : 'Closed'}
                          </span>
                        )}
                      </div>
                      <div className={`text-xs text-gray-400 mt-1 ${isRTL ? 'text-right' : 'text-left'}`} dir="ltr">
                        {formatDateRange(fiscalYear.startDate, fiscalYear.endDate, { isRTL })}
                      </div>
                    </div>
                    {selectedFiscalYear?.id === fiscalYear.id && (
                      <FiCheckCircle className="text-purple-400 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
          </div>
        )}
      </div>

      <div className="px-4 py-2 border-t border-gray-700">
        <button
          onClick={handleManageFiscalYears}
          className={`w-full text-xs text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-1 ${
            isRTL ? 'justify-end flex-row-reverse' : 'justify-end'
          }`}
        >
          <span>{isRTL ? 'إدارة السنوات المالية' : 'Manage Fiscal Years'}</span>
          <span className={isRTL ? '' : 'rotate-180'}>→</span>
        </button>
      </div>
    </div>
  ) : null

  return (
    <div className="relative" ref={triggerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md transition-colors ${
          isOpen
            ? 'bg-purple-900/50 border border-purple-500/50'
            : 'bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700'
        }`}
        title={isRTL ? 'اختر السنة المالية' : 'Select Fiscal Year'}
      >
        <FiCalendar className="text-purple-400 text-[10px]" />
        <span className="text-[11px] font-bold text-gray-300 max-w-[120px] truncate">
          {selectedFiscalYear
            ? selectedFiscalYear.name
            : (isRTL ? 'اختر السنة المالية' : 'Select Fiscal Year')}
        </span>
        {selectedFiscalYear?.isClosed && (
          <span className="text-[9px] px-1 py-0.5 rounded bg-red-900/30 text-red-300">
            {isRTL ? 'مغلقة' : 'Closed'}
          </span>
        )}
        <FiChevronDown className={`text-gray-400 text-[10px] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {typeof document !== 'undefined' && dropdown && createPortal(dropdown, document.body)}
    </div>
  )
}
